import React from 'react'
import { motion } from 'framer-motion'
import { 
  FiInstagram, 
  FiTwitter, 
  FiLinkedin, 
  FiFacebook,
  FiMail,
  FiPhone,
  FiMapPin
} from 'react-icons/fi'
import { usePublicContent } from '../hooks/usePublicContent'
import { FooterContent } from '../types/content'

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear()
  
  // Get footer content from CMS
  const { content, loading } = usePublicContent('footer')
  const footerData = content?.data as FooterContent

  // Use CMS data with fallbacks
  const quickLinks = footerData?.quickLinks?.sort((a, b) => a.order - b.order) || [
    { name: 'About Us', href: '#about', order: 1 },
    { name: 'Our Artists', href: '#artists', order: 2 },
    { name: 'Pricing', href: '#pricing', order: 3 },
    { name: 'Contact', href: '#contact', order: 4 },
  ]

  // Map social links from CMS data
  const iconMap = {
    FiInstagram,
    FiTwitter, 
    FiLinkedin,
    FiFacebook
  }

  const socialLinks = footerData?.socialLinks?.filter(link => link.url).map(link => ({
    icon: iconMap[link.icon as keyof typeof iconMap] || FiInstagram,
    href: link.url,
    label: link.platform,
    color: 'hover:text-white'
  })) || []

  const scrollToSection = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  // Show loading state
  if (loading) {
    return (
      <footer className="bg-neutral-800 text-white">
        <div className="container-custom py-12 sm:py-16">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-4">
              <div className="animate-pulse bg-neutral-700 h-20 w-32 rounded"></div>
              <div className="animate-pulse bg-neutral-700 h-4 w-3/4 rounded"></div>
              <div className="animate-pulse bg-neutral-700 h-4 w-1/2 rounded"></div>
            </div>
            <div className="space-y-4">
              <div className="animate-pulse bg-neutral-700 h-6 w-24 rounded"></div>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse bg-neutral-700 h-4 w-16 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer className="bg-neutral-800 text-white">
      {/* Main Footer Content */}
      <div className="container-custom py-6 sm:py-8">
        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-3 sm:mb-4"
            >
              <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                <img 
                  src={footerData?.logo || "/assets/LOGO - TRANSPARENT WHITE.png"} 
                  alt={footerData?.logoAlt || "O-Live Music Booking"} 
                  className="h-12 sm:h-14 w-auto -ml-1"
                />
                <span className="text-base sm:text-lg font-bold font-display"></span>
              </div>
              <p className="text-neutral-300 mb-3 sm:mb-4 text-xs sm:text-sm leading-relaxed">
                {footerData?.companyDescription || "Elevating artists and creating unforgettable musical experiences. We're passionate about connecting extraordinary talent with audiences who appreciate authentic artistry."}
              </p>
              
              {/* Contact Info */}
              <div className="space-y-1 text-xs text-neutral-300">
                {footerData?.contactInfo?.email?.[0] && (
                  <div className="flex items-center space-x-2">
                    <FiMail size={12} className="flex-shrink-0" />
                    <span className="break-all">{footerData.contactInfo.email[0]}</span>
                  </div>
                )}
                {footerData?.contactInfo?.phone?.[0] && (
                  <div className="flex items-center space-x-2">
                    <FiPhone size={12} className="flex-shrink-0" />
                    <span>{footerData.contactInfo.phone[0]}</span>
                  </div>
                )}
                {footerData?.contactInfo?.address?.[0] && (
                  <div className="flex items-center space-x-2">
                    <FiMapPin size={12} className="flex-shrink-0" />
                    <span>{footerData.contactInfo.address[0]}</span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-wrap gap-1.5 sm:gap-2"
            >
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-1.5 sm:p-2 bg-neutral-700 rounded-lg text-neutral-400 hover:text-white transition-colors duration-200 min-h-[36px] min-w-[36px] flex items-center justify-center"
                  aria-label={social.label}
                >
                  <social.icon size={14} className="sm:w-4 sm:h-4" />
                </motion.a>
              ))}
            </motion.div>
          </div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-semibold text-white mb-2 sm:mb-3 text-sm sm:text-base">Quick Links</h3>
            <ul className="space-y-1">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-neutral-400 hover:text-white transition-colors duration-200 text-xs min-h-[32px] flex items-center"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>


      {/* Bottom Bar */}
      <div className="border-t border-neutral-700 bg-neutral-900">
        <div className="container-custom py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 gap-2">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="text-neutral-400 text-xs flex items-center space-x-1 text-center sm:text-left"
            >
              <span>{footerData?.copyrightText || `© ${currentYear} O-Live Music Agency.`}</span>
              
              <span></span>
            </motion.p>
            
            <motion.button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="text-neutral-400 hover:text-primary transition-colors duration-200 text-xs font-medium min-h-[32px] flex items-center justify-center px-3"
            >
              Back to Top ↑
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
